import React, {useCallback, useEffect, useMemo} from "react";
import { Row, Col, Card, CardHeader, CardBody } from 'reactstrap';
import "react-toastify/dist/ReactToastify.css";
import Button from "react-bootstrap/Button";
import RightModal from "./modalForm";

// hooks
import { useRedux, useHistory, useFareRestriction, useSearchInput } from "../../../hooks";

import {
    editDataSaleIntegration,
    getContentPeerChannels,
    getSaleCurrencies,
    getSaleDeliverySystems,
    getSaleIntegrationHandlers,
    getSaleIntegrations,
    getSalePaymentSystems,
    getSalePriceTypes,
    getSaleStores, onInvalidateCacheSaleIntegration,
    onRemoveSaleIntegration,
    removeSaleIntegrationModalHide,
    removeSaleIntegrationModalShow,
    showRightModalSaleIntegration
} from "../../../redux/actions";

import TableList from "../../../components/TableList";
import SaleIntegrationColumns from "./tableColumns";
import {SaleIntegrationType} from "../../../redux/Sale/Integration/types";
import {EntityActionsHandlersType} from "../../../redux/types";
import RemoveModal from "../../../components/RemoveForm";

import '../../../i18/config';
import {useTranslation} from "react-i18next";
import BreadCrumb from "../../../components/Common/BreadCrumb";
import Loader from "../../../components/Loader";
import {LicenseProductParam} from "../../../redux/Sale/License/types";
import UpgradeFarePage from "../../../components/UpgradeFarePage";
import MainListFilter from "../../../components/MainListFilter";


const SaleIntegrationIndex = () => {
    const { dispatch, useAppSelector } = useRedux();

    const { t } = useTranslation(['common']);

    const componentType = 'saleIntegrationTable';

    const {
        historyHash,
        searchPhrase,
        loadItems,
        handleSearchClick
    } = useHistory({
        componentType,
        loadAction: getSaleIntegrations,
        dispatch
    });

    const { onSearchInput } = useSearchInput(handleSearchClick);

    const {
        saleIntegration,
        saleIntegrationHandler,
        contentPeerChannels,
        saleStore,
        saleCurrency,
        salePriceType,
        salePaymentSystem,
        saleDeliverySystem,
        salePlan
    } = useAppSelector(state => ({
        saleIntegration: state.SaleIntegration,
        saleIntegrationHandler: state.SaleIntegrationHandler,
        contentPeerChannels: state.ContentPeerChannel,
        saleStore: state.SaleStore,
        saleCurrency: state.SaleCurrency,
        salePriceType: state.SalePriceType,
        salePaymentSystem: state.SalePaymentSystem,
        saleDeliverySystem: state.SaleDeliverySystem,
        salePlan: state.SalePlan
    }));

    const { upgradeFare } = useFareRestriction(
        salePlan?.currentPlan,
        LicenseProductParam.SALE_MAX_WIDGETS
    );

    const isFetched = useMemo(() => (
        saleStore.isFetched &&
        saleCurrency.isFetched &&
        salePriceType.isFetched &&
        contentPeerChannels.isFetched &&
        salePaymentSystem.isFetched &&
        saleDeliverySystem.isFetched &&
        saleIntegrationHandler.isFetched
    ), [
        saleStore.isFetched,
        saleCurrency.isFetched,
        salePriceType.isFetched,
        contentPeerChannels.isFetched,
        salePaymentSystem.isFetched,
        saleDeliverySystem.isFetched,
        saleIntegrationHandler.isFetched
    ]);

    useEffect(() => {

        dispatch(getContentPeerChannels());
        dispatch(getSaleStores({maxResults:1000}));
        dispatch(getSaleCurrencies({maxResults:1000}));
        dispatch(getSalePriceTypes({maxResults: 1000}));
        dispatch(getSalePaymentSystems({maxResults:1000}));
        dispatch(getSaleDeliverySystems({maxResults:1000}));
        dispatch(getSaleIntegrationHandlers());
    }, [dispatch]);

    const openRightModal = useCallback((event:any) => {
        if ((event !== undefined) && (event.uuid !== undefined) && (event.uuid.length>0)){
            dispatch(editDataSaleIntegration({data:event, modalEditTitle: 'changeIntegration'}));
        } else {
            dispatch(showRightModalSaleIntegration({data:[], modalEditTitle: 'newIntegration'}));
        }
    }, [dispatch]);

    const onEntityRemoveModalShow = useCallback((event:any) => {
        if ((event !== undefined) && (event.uuid !== undefined) && (event.uuid.length>0)){
            dispatch(removeSaleIntegrationModalShow({data:event, modalEditTitle: 'deleteIntegration'}));
        }
    }, [dispatch]);

    const onEntityRemoveModalHide = useCallback(() => dispatch(removeSaleIntegrationModalHide()), [dispatch]);


    const onEntityRemove = useCallback((event:any) => {
        if ((event !== undefined) && (event.uuid !== undefined) && (event.uuid.length>0)){
            dispatch(onRemoveSaleIntegration({data:event, modalEditTitle: 'deleteIntegration'}));
        }
    }, [dispatch]);

    const onInvalidateCache = useCallback((event:any) => {
        if ((event !== undefined) && (event.uuid !== undefined) && (event.uuid.length>0)){
            dispatch(onInvalidateCacheSaleIntegration({data:event, modalEditTitle: 'invalidateCache'}));
        }
    }, [dispatch]);

    useEffect(() => {
        if(saleIntegration.isNeedReload) {
            loadItems();
        }
    }, [saleIntegration.isNeedReload, loadItems]);

    const EntityActionsHandlers:EntityActionsHandlersType = {
        onEdit: openRightModal,
        onRemove: onEntityRemoveModalShow,
        onInvalidateCache: onInvalidateCache
    }

    const onTableRowClick = useCallback((item:SaleIntegrationType) => {
        if (item.actions && item.actions.canEdit){
            openRightModal(item);
        }
    }, [openRightModal]);

    const columns = SaleIntegrationColumns(EntityActionsHandlers);

    return (
        <>
            <BreadCrumb title={t('menu:integrations')} pageTitle={t('menu:main')} />

            { upgradeFare && isFetched ?
                <>
                    <div className="d-flex flex-row flex-grow-1">
                        <div className="alert alert-danger alert-border-left alert-dismissible fade show d-flex flex-row flex-grow-1" role="alert">
                            <i className="ri-error-warning-line me-2 align-middle"></i> <strong className="pe-2">{t('common:attention')}!</strong>{t('plan:restrictionWidgetsText')}
                        </div>
                    </div>
                    <UpgradeFarePage/>
                </>
                :
                <>
                <Row className="g-4 mb-3">
                    <div className="col-sm-auto">
                        <div>
                            <Button variant="secondary" onClick={openRightModal} className="btn btn-success">
                                <i className="ri-add-line align-bottom me-1"/> {t('common:addNew')}
                            </Button>
                        </div>
                    </div>
                </Row>

                <Row>
                    <Col lg={12}>
                        <Card>
                            <CardHeader className="border-bottom-0">
                                <h5 className="card-title mb-0">{t('integration:integrations')}</h5>
                            </CardHeader>
                            <CardBody className="border border-dashed border-end-0 border-start-0 pb-3">
                                    <MainListFilter
                                        onClick={handleSearchClick}
                                        value={searchPhrase}
                                        onChange={onSearchInput}
                                    />
                            </CardBody>
                            <CardBody>
                                {isFetched ?
                                    <TableList
                                    columns={columns}
                                    data={saleIntegration.items}
                                    onRowClick={onTableRowClick}
                                    key={componentType}
                                    historyHash = {historyHash}
                                    isShowChangePageSize={true}
                                    />
                                    :
                                    <Loader/>
                                }
                            </CardBody>
                        </Card>
                    </Col>
                </Row>

                <RightModal/>
                <RemoveModal
                    onRemove={onEntityRemove}
                    handleClose={onEntityRemoveModalHide}
                    removeItem={saleIntegration.selectedItem}
                    isOpen={saleIntegration.removeModalShow}
                    modalTitle={saleIntegration.modalEditTitle}/>
                    </>
            }
        </>
    );
};

export default SaleIntegrationIndex;


